import { Breadcrumb, Button, Page, Typography } from "@eisox/design-system";
import { NutIcon } from "@eisox/icons";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { boilerRoomApi, heatingNetworkApi } from "~/api";
import { Mode } from "~/components/live-history-toggle";
import { RealtimeUnavailableDialog } from "~/components/realtime-unavailable-dialog";
import { BoilerRoomProvider, useBoilerRoomContext } from "~/features/boiler-room";
import {
  getEcsCapabilities,
  HeatingNetworkProvider,
  PlanningCard,
  SchemeCard,
  Settings,
  useHeatingNetworkContext,
} from "~/features/heating-network";
import { HeatingNetworkConfigurationDialog } from "~/features/heating-network-configuration";
import { ECSSetpointCard } from "~/features/heating-network/components/ecs-setpoint-card";
import { HeatingCurveCard } from "~/features/heating-network/components/heating-curve-card";
import { requirePagePermission } from "~/helpers/auth/route-permission.helper";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute(
  "/_authenticated/buildings_/$buildingId/boiler-rooms_/$boilerRoomId_/heating-networks/$heatingNetworkId",
)({
  beforeLoad: ({ context: { permissions }, params: { buildingId }, preload }) => {
    requirePagePermission(permissions, "boilerroom", buildingId, preload);
  },
  loader: async ({ context: { queryClient }, params: { buildingId, heatingNetworkId } }) => {
    const boilerRooms = await queryClient.ensureQueryData(boilerRoomApi.queries.building(buildingId));
    await Promise.all([
      queryClient.ensureQueryData(heatingNetworkApi.queries.plannings(heatingNetworkId)),
      queryClient.ensureQueryData(heatingNetworkApi.queries.heatingCurves(heatingNetworkId)),
      ...boilerRooms.map(boilerRoom => queryClient.ensureQueryData(boilerRoomApi.queries.boilerRoom(boilerRoom.id))),
    ]);
  },
  head: async ({ match: { context }, params: { buildingId, heatingNetworkId } }) => {
    const heatingNetworks = await context.queryClient.ensureQueryData(heatingNetworkApi.queries.building(buildingId));
    return buildingPageHead(
      context.queryClient,
      buildingId,
      heatingNetworks.find(heatingNetwork => heatingNetwork.id === heatingNetworkId)?.name ?? m.heating_network(),
    );
  },
  component: RouteComponent,
});

function HeatingNetwork() {
  const navigate = Route.useNavigate();
  const { buildingId, boilerRoomId, heatingNetworkId } = Route.useParams();
  const { floors, boilerRooms, permissions } = useBuildingContext(Route.id);
  const { boilerRoom, isRealtimeStale, liveSampleDate } = useBoilerRoomContext(Route.id);
  const { heatingNetwork, ecs } = useHeatingNetworkContext(Route.id);

  const floor = floors.find(f => boilerRooms.find(b => b.id === boilerRoom.id)?.floor?.id === f.id);

  const { hasSetpoint, hasThermalShock } = getEcsCapabilities(heatingNetwork);

  const showSetpoint = !ecs || hasSetpoint;
  const showPlanning = !ecs || hasThermalShock;

  return (
    <Page.Root>
      <Page.Header className="flex-nowrap [&>:not(:first-child)]:shrink-0">
        <Breadcrumb.Root className="min-w-0 overflow-hidden">
          <Breadcrumb.List className="min-w-0">
            {floor && (
              <>
                <Breadcrumb.Item className="shrink-0">
                  <Breadcrumb.Link asChild>
                    <Link to={"/buildings/$buildingId/floors/$floorId"} params={{ buildingId, floorId: floor.id }}>
                      {floor.name}
                    </Link>
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator className="shrink-0" />
              </>
            )}
            <Breadcrumb.Item className="shrink-0">
              <Breadcrumb.Link asChild>
                <Link to={"/buildings/$buildingId/boiler-rooms/$boilerRoomId"} params={{ buildingId, boilerRoomId }}>
                  {boilerRoom.name}
                </Link>
              </Breadcrumb.Link>
            </Breadcrumb.Item>
            <Breadcrumb.Separator className="shrink-0" />
            <Breadcrumb.Item className="min-w-0">
              <Breadcrumb.Page asChild>
                <Page.Title asChild>
                  <Typography className="min-w-0">{heatingNetwork.name}</Typography>
                </Page.Title>
              </Breadcrumb.Page>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb.Root>
        <Settings>
          <Button>
            {m.settings()} <NutIcon />
          </Button>
        </Settings>
        {!ecs && (
          <HeatingNetworkConfigurationDialog heatingNetworkId={heatingNetworkId}>
            <Button className="ml-auto">
              {permissions.valve?.heatingNetworkId?.update
                ? m.configure_associated_valves()
                : m.view_associated_valves()}
              <NutIcon />
            </Button>
          </HeatingNetworkConfigurationDialog>
        )}
      </Page.Header>
      <Page.Content className="gap-5 min-[1633px]:mt-0 min-[1633px]:mb-10 min-[1633px]:ml-10 flex flex-col overflow-y-auto bg-transparent min-[1633px]:flex-row min-[1633px]:overflow-y-hidden min-[1633px]:rounded-l-[10px]">
        <div className="gap-5 min-[1633px]:min-h-0 min-[1633px]:min-w-0 flex w-full flex-none flex-col min-[1633px]:flex-1">
          <SchemeCard className="p-5 min-[1633px]:min-h-0 shrink-0 min-[1633px]:flex-1" buildingId={buildingId} />
          {showSetpoint &&
            (ecs ? (
              <ECSSetpointCard className="min-[1633px]:min-h-0 shrink-0 min-[1633px]:flex-1" />
            ) : (
              <HeatingCurveCard className="min-[1633px]:min-h-0 shrink-0 min-[1633px]:flex-1" />
            ))}
        </div>
        {showPlanning && <PlanningCard className="min-[1633px]:min-h-0 min-[1633px]:basis-122.5 w-full shrink-0" />}
      </Page.Content>
      <RealtimeUnavailableDialog
        open={isRealtimeStale}
        buildingId={buildingId}
        lastSampleDate={liveSampleDate}
        onGoToHistory={() => {
          void navigate({ search: prev => ({ ...prev, mode: Mode.History }) });
        }}
      />
    </Page.Root>
  );
}

function RouteComponent() {
  const { buildingId, boilerRoomId, heatingNetworkId } = Route.useParams();
  const { data: boilerRoomSummaries } = useSuspenseQuery(boilerRoomApi.queries.building(buildingId));
  const boilerRooms = useSuspenseQueries({
    queries: boilerRoomSummaries.map(boilerRoom => boilerRoomApi.queries.boilerRoom(boilerRoom.id)),
  }).map(query => query.data);
  const { data: heatingCurves } = useSuspenseQuery(heatingNetworkApi.queries.heatingCurves(heatingNetworkId));
  const { data: plannings } = useSuspenseQuery({ ...heatingNetworkApi.queries.plannings(heatingNetworkId) });

  return (
    <BoilerRoomProvider
      boilerRooms={boilerRooms}
      boilerRoomId={boilerRoomId}
      mode={Mode.Live}
      onModeChange={() => {}}
      onDateChange={() => {}}
    >
      <HeatingNetworkProvider heatingNetworkId={heatingNetworkId} plannings={plannings} heatingCurves={heatingCurves}>
        <HeatingNetwork />
      </HeatingNetworkProvider>
    </BoilerRoomProvider>
  );
}
